import React, { useState, useEffect, useContext, useRef }  from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import LayoutNavSectionCombinationList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationList.jsx';
import SelectedList from 'components/layout_and_quote_builder/quote_builder/SelectedList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import ChooseLayoutDimensions from 'components/layout_and_quote_builder/options/ChooseLayoutDimensions.jsx';
import ManufacturingLineLists from 'components/frank/ManufacturingLineLists.jsx';
import GeneralNavSection from 'components/layout_and_quote_builder/quote_builder/GeneralNavSection.jsx';
import { shouldShowSectionLabel } from 'components/layout_and_quote_builder/utils/navSectionUtils.js';
import { reportMissingBobbinAnswer } from 'components/layout_and_quote_builder/utils/reportMissingBobbinAnswer.js';
import { DebugPanel } from 'components/layout_and_quote_builder/DebugPopup.jsx';
import { readSectionBobbin, writeSectionBobbin } from 'components/frank/utils/manufacturingLineListUrlState.js';

const LayoutAndQuoteBuilderNavSection = ( props ) => {
  const myLayoutContext = useContext(LayoutContext);

  // const fallbackMaterialValues = () => {
  //   return {
  //     layout_dimension_ids: [21],
  //     layout_dimension_unit: myLayoutContext.layoutOptions.layout_dimension_unit,
  //     layout_dimension_top_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,

  //     layout_dimension_top_margin_printing: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_printing: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_printing: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,
  //   }
  // }

  // Use props if provided, otherwise use local state (for backwards compatibility)
  const selectedList = props.selectedList !== undefined ? props.selectedList : [];
  const setSelectedList = props.setSelectedList || (() => {});

  const [materialLayoutDimensions, setMaterialLayoutDimensions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // The bobbin checkbox value, kept here because it is an input to BOTH displays below it: the "Chosen
  // material dimensions" and (in Frank mode) the manufacturing lines found over those same sheets. Undefined
  // until the user touches the checkbox -> the part_type default.
  //
  // On /mcc it is restored from the address bar, with the machine-type and reduction ticks it shares a filter
  // block with (see manufacturingLineListUrlState): the bobbin chooses the material sheets the whole listing is
  // found over, so a section that remounts -- closing the guided setup wizard, a refresh, a shared link --
  // without it would come back listing over different sheets than the ones on screen.
  const [bobbinEnabled, setBobbinEnabled] = useState(
    () => (window.frank_mode ? readSectionBobbin(props.navSection.index) : undefined)
  );

  // The bobbin checkbox is an input to the material-sheet finder, so re-fetching with the toggle value
  // recomputes "Chosen material dimensions" (bobbin true/false changes the result). Absent on mount ->
  // the part_type default.
  //
  // Toggling the bobbin fires this straight away (it is not on the listing's debounce), so two toggles can
  // leave two requests in flight -- LATEST WINS: each call takes a sequence number and only the newest applies
  // its answer, so a slow earlier response cannot land stale dimensions over a newer one.
  const materialFetchSequenceRef = useRef(0);
  const fetchDefaultMaterialValues = async (bobbinEnabled) => {
    if(props.navSection.index == undefined) {
      return ;
    }
    const sequence = materialFetchSequenceRef.current + 1;
    materialFetchSequenceRef.current = sequence;
    try {
      const bobbinQuery = bobbinEnabled === undefined ? '' : `?bobbin_enabled=${bobbinEnabled}`;
      // Frank mode (the /mcc page) computes the "Chosen material dimensions" through
      // Frank::SectionManufacturingLineFinderSheets via Frank's own endpoint; the legacy page keeps
      // LayoutCombinationsFinder::DefaultMaterialValues.
      const materialSheetsUrl = window.frank_mode
        ? `/mcc/${window.user_quote_id}/section-material-sheets/${props.navSection.index}${bobbinQuery}`
        : `/manufacturing-cost-calculator/${window.user_quote_id}/default-material-values/${props.navSection.index}${bobbinQuery}`;
      const response = await fetch(materialSheetsUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch default material values');
      }
      const data = await response.json();
      if (sequence !== materialFetchSequenceRef.current) { return; } // a newer toggle superseded this answer
      // Both rows below draw their bobbin checkbox from this payload's answer, so a payload that forgot it
      // would take the customer's bobbin choice away in silence. Checked HERE, once per response, rather than
      // in the rows -- they re-render many times per response.
      reportMissingBobbinAnswer(data);
      // Merge so a re-fetch on toggle refreshes the computed sheet without dropping the bobbin_enabled /
      // filtering flags ChooseLayoutDimensions writes into this object for the combinations POST.
      setMaterialLayoutDimensions((previous) => ({ ...(previous || {}), ...data }));
    } catch (error) {
      console.error('Error fetching default material values:', error);
    } finally {
      if (sequence === materialFetchSequenceRef.current) { setIsLoading(false); }
    }
  };

  // Fetch default material values from API on mount (part_type default bobbin, or the one restored from the
  // address bar -- the sheets must be found over the very bobbin value the listing below is found with).
  useEffect(() => {
    fetchDefaultMaterialValues(bobbinEnabled);
  }, []);

  // FRANK bobbin ownership. On /mcc the bobbin question is not its own box at the top of the section: it is the
  // first row of the unified section-filter block ManufacturingLineLists draws (with the printing- and
  // die-cutting-machine rows), so all three read as one control and lock together while a change re-lists.
  // Whether that row is drawn, whether it is editable and how it starts are ANSWERED BY THE SERVER
  // (BobbinGating) and arrive with the sheets, so the checkbox cannot disagree with the bobbin state the
  // manufacturing lines were found for. Absent until the first payload lands -> the row is not drawn yet; a
  // payload that lands WITHOUT one is reported at the fetch above rather than quietly dropping the row.
  const partType = materialLayoutDimensions && materialLayoutDimensions.part_type;
  const bobbinAnswer = (materialLayoutDimensions && materialLayoutDimensions.bobbin) || {};
  const bobbinShown = bobbinAnswer.shown === true;
  const bobbinForced = bobbinAnswer.forced === true;
  const bobbinDefaultEnabled = bobbinAnswer.default_enabled === true;
  // `bobbinEnabled` undefined means "not touched" -> the part_type default. The fetches still send nothing then,
  // so the server applies the same default in ONE place; only the checkbox needs the resolved value to show.
  const bobbinChecked = bobbinForced ? true : (bobbinEnabled === undefined ? bobbinDefaultEnabled : bobbinEnabled);

  // Give the bobbin question back to the part_type: forget the manual choice and drop it from the address bar
  // too, so the next mount does not restore what was just discarded.
  const dropBobbinChoice = () => {
    setBobbinEnabled(undefined);
    writeSectionBobbin(props.navSection.index, undefined);
  };

  // When the part_type CHANGES, drop any manual bobbin choice back to that part_type's default -- the same reset
  // the legacy ChooseLayoutDimensions row does. Frank only: the legacy row owns its own state.
  //
  // The part_type becoming KNOWN is not a change: it is empty until the material sheets land, and resetting on
  // that first resolution wiped a bobbin restored from the address bar a moment after mount -- the section would
  // then list over the part_type's default sheets while the URL still said otherwise. `== undefined` because the
  // not-known-yet value is null (materialLayoutDimensions is null before the fetch lands), the same loose test
  // bobbinGating uses on it.
  const previousPartTypeRef = useRef(partType);
  useEffect(() => {
    if (!window.frank_mode) { return; }
    const previousPartType = previousPartTypeRef.current;
    previousPartTypeRef.current = partType;

    // A part_type that HIDES the bobbin question (corrugated_cardboard_offset) has no bobbin answer to give, so a
    // value the address bar carried in is dropped rather than applied: it draws no row, yet it would still be
    // sent with the material-sheet and listing requests -- a tick nobody can see or undo. The sheets are re-found
    // without it, because the ones on screen were found over exactly that value. Guarded on there being a value,
    // so an ordinary section whose row is hidden does nothing at all.
    if (partType != undefined && !bobbinShown && bobbinEnabled !== undefined) {
      dropBobbinChoice();
      fetchDefaultMaterialValues(undefined);
      return;
    }

    if (previousPartType == undefined || previousPartType === partType) { return; }
    dropBobbinChoice();
  }, [partType, bobbinShown, bobbinEnabled]);

  // What ManufacturingLineLists needs to draw the bobbin as the first section-filter row.
  const frankBobbin = {
    shown: bobbinShown,
    forced: bobbinForced,
    // `enabled` is the RAW value (undefined until touched -> part_type default) the listing is found over;
    // `checked` is the resolved value the checkbox shows. One descriptor carries both, so the child never needs
    // a second bobbinEnabled prop kept in step with it.
    enabled: bobbinEnabled,
    checked: bobbinChecked,
    // A TICK IS ONLY AN EDIT: it moves the checkbox and the address bar (so the choice comes back with the rest
    // of the section's filters), and nothing else. It deliberately does NOT re-find the material sheets --
    // "Chosen material dimensions" would then describe a bobbin the manufacturing lines below were not found
    // over, which is the mismatch the Search button exists to end.
    onChange: (value) => {
      setBobbinEnabled(value);
      writeSectionBobbin(props.navSection.index, value);
    },
    // ...and Search is when it becomes a question: the sheets are re-found for the value the listing is about to
    // be found over, so both halves of the section move on the same press.
    onSearch: () => fetchDefaultMaterialValues(bobbinEnabled),
  };

  const navSectionLabel = () => {
    if(props.navSection.label) {
      return <span className={styles.navSectionLabel}> {props.navSection.label} </span>
    }
  }

  const chooseLayoutDimensionsComponent = () => {
    return <ChooseLayoutDimensions
      defaultValues={materialLayoutDimensions}
      publicSetter={setMaterialLayoutDimensions}
      onBobbinChange={(value) => {
        setBobbinEnabled(value);
        fetchDefaultMaterialValues(value);
      }}
      index={props.index}
    />
  }

  // Whether to show the material sheet finder's admin sanity displays (the resolved part_type and the
  // chosen material sheets). Admins always see them; users with the quote_builder_v2_enabled UserParam are
  // treated like admins on this Manufacturing Cost Calculator page for now, so they see them too. The
  // flag is undefined on pages that do not set it, which correctly keeps these displays hidden there.
  const canSeeSectionInsights = window.current_user_admin || window.quote_builder_v2_enabled;

  // Append the section's part_type to its title (e.g. "Corrugated cardboard
  // (corrugated_cardboard_flexo)"), so a reviewer can see exactly which part_type a section resolved to
  // and flag anything wrong. Hidden from customers.
  const adminPartTypeSuffix = () => {
    if (!canSeeSectionInsights || !props.navSection.part_type) {
      return null;
    }

    return (
      <span style={{ fontSize: '14px', color: '#888', fontWeight: 'normal' }}>
        {' '}({props.navSection.part_type})
      </span>
    );
  }

  // A single material-sheet chip (e.g. "B1 707mm X 1000mm" or the "Üretilemiyor" indicator).
  const materialDimensionChip = (text, key) => (
    <div
      key={key}
      style={{
        fontSize: '13px',
        color: '#555',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '4px 12px',
        backgroundColor: '#f9f9f9',
      }}
    >
      {text}
    </div>
  );

  // Sanity check: the material sheet dimensions computed for this section are hidden from customers,
  // but shown to admins (and quote-builder-v2 users, per canSeeSectionInsights) next to the section
  // heading so they can eyeball the chosen sheets and flag anything that looks off. The sheets are
  // grouped by sub_section, titled by its label (the same "Cover" / "Tray" / "Tray & Cover" the section's
  // tabs use), so a section with an oversize combined nesting still shows the producible per-part sheets
  // instead of one whole-section "cannot produce". Falls back to the flat list for any endpoint that does
  // not send groups. Nothing renders when the viewer is not permitted or when nothing was computed.
  const adminChosenMaterialDimensionsLine = () => {
    if (!canSeeSectionInsights) {
      return null;
    }

    const groups = materialLayoutDimensions?.material_sheet_groups || [];
    const flatSheets = materialLayoutDimensions?.layout_dimensions || [];
    const renderableGroups = groups.filter((group) => group.dimensions && group.dimensions.length > 0);
    const hasGroups = renderableGroups.length > 0;

    // NOTHING WAS COMPUTED. Two situations reach here and must read differently: the fetch has not answered
    // yet, and it answered with no sheets. A blank space where the dimensions belong looks like the page
    // errored, so once the fetch has actually ANSWERED (we are no longer loading and hold a response) say so
    // plainly -- an empty result is a legitimate answer (e.g. no bobbin width holds the dieline), not a
    // failure. While it is still loading, stay quiet. /mcc only: the legacy page's empty state is left as it
    // was.
    if (!hasGroups && flatSheets.length === 0) {
      if (!window.frank_mode || isLoading || !materialLayoutDimensions) {
        return null;
      }

      return (
        <center>
          <div
            style={{ marginTop: '-10px', marginBottom: '10px', fontSize: '12px', color: '#888' }}
            title="No material sheet was found for this section's dieline -- an empty result, not an error."
          >
            Material sheets cannot be found.
          </div>
        </center>
      );
    }

    return (
      <center>
        <div
          style={{ marginTop: '-10px', marginBottom: '10px' }}
          title="The material sheet dimensions computed for this section, per part. Flag if anything looks off."
        >
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
            Chosen material dimensions:
          </div>

          {hasGroups ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              {renderableGroups.map((group, groupIndex) => {
                const partLabel = group.label || (group.part_names?.length ? group.part_names.join(', ') : null);

                return (
                  <div
                    key={partLabel || groupIndex}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
                  >
                    {partLabel && (
                      <div style={{ fontSize: '12px', color: '#555', fontWeight: 'bold' }}>
                        {partLabel}
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {group.dimensions.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, `${sheet.description}-${sheetIndex}`))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              {flatSheets.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, sheetIndex))}
            </div>
          )}
        </div>
      </center>
    );
  }

  if(props.navSection.part_type == "general") {
    return <GeneralNavSection totalNavSectionSize={props.totalNavSectionSize} />
  }

  // Show loading state while fetching default material values
  if (isLoading || !materialLayoutDimensions) {
    return (
      <div className={styles.LayoutAndQuoteBuilderNavSection}>
        <div className={styles.nav_section}>
          {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
            <center> <h2> { props.navSection.label} </h2> </center>
          )}
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {t('quote_builder.loading')}...
          </div>
        </div>
      </div>
    );
  }

  const sectionBody = () => (
    <>
      { selectedList.length == 0 && <LayoutNavSectionCombinationList
        navSection={props.navSection}
        index={props.index}
        count={props.count}
        setSelectedList={setSelectedList}
        materialLayoutDimensions={materialLayoutDimensions}
      /> }

      <SelectedList selectedList={selectedList} setSelectedList={setSelectedList} config={props.config} index={props.index} totalNavSectionSize={props.totalNavSectionSize}/>
    </>
  );

  return (
    <>
    <div className={styles.LayoutAndQuoteBuilderNavSection}>

      <div className={styles.nav_section}>

        {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
          <center> <h2> { props.navSection.label}{adminPartTypeSuffix()} </h2> </center>
        )}

        {/* Legacy page: the bobbin checkbox is an INPUT to the material-sheet finder, so it sits ABOVE the
            resulting "Chosen material dimensions" line, which recomputes when the checkbox toggles. On /mcc the
            bobbin moves into ManufacturingLineLists' unified section-filter block, so it is not drawn here. */}
        {!window.frank_mode && (
          <div className={styles.nav_section_layout_selector}> <span> {chooseLayoutDimensionsComponent()} </span> </div>
        )}

        <DebugPanel title={props.navSection.label}>{adminChosenMaterialDimensionsLine()}</DebugPanel>

        {/* Frank lists the manufacturing lines it found under the material sheets (verify view; no selection
            yet). It takes the bobbin -- both the value it re-lists over and the row it draws in its filter
            block -- so the listing recomputes over the very sheets the bobbin chooses. */}
        {window.frank_mode && (
          <ManufacturingLineLists
            navSectionIndex={props.navSection.index}
            count={props.count}
            bobbin={frankBobbin}
            config={props.config}
            stageView={props.stageView}
            inWizard={props.inWizard}
            onWizardPickedChange={props.onWizardPickedChange}
            onWizardSelectionChange={props.onWizardSelectionChange}
            onLinePicked={props.onLinePicked}
            onSolutionPicked={props.onSolutionPicked}
            onGoToNextStage={props.onGoToNextStage}
            onGoToManufacturingLine={props.onGoToManufacturingLine}
          />
        )}

         <hr />
        {/* Frank mode shows ONLY the section material sheets above; the manufacturing lines + solutions in
            sectionBody() are hidden until Frank rebuilds those services. */}
        { !window.frank_mode && sectionBody() }

      </div>
    </div>
    <br />
    <br />
    </>
  )
}

export default LayoutAndQuoteBuilderNavSection;
